import React, { FunctionComponent, ReactNode, useMemo } from "react";

import { bridge } from "@multiweb/reactCore/bridge";
import { bemClassNames, bemQANames, createQA, useStopPropagationEventListenerRef } from "@multiweb/reactUtils";
import { FlexGrow, FlexShrink, Layout, SpacingSize } from "../Layout";

const { prettyPrintFileSize, splitFileName } = bridge;

const classNames = bemClassNames("file-link");
const fileLink = classNames();
const fileLinkLink = classNames("link");
const fileLinkSize = classNames("size");
const fileLinkName = classNames("name");
const fileLinkExtension = classNames("extension");

const qa = bemQANames("file-link");
const qaMain = qa();
const qaLink = qa("link");
const qaSize = qa("size");
const qaSignature = qa("signature");

export type FileLinkProps = {
    /** Název souboru */
    fileName: string;
    /** Odkaz ke stažení souboru (je potřeba například v momentě, kdy se na odkaz klikne kolečkem) */
    fileDownloadLink?: string;
    /** Akce, která se vyvolá při kliknutí na odkaz se souborem */
    onDownloadRequested: () => void;
    /** Velikost souboru (v bytech) */
    fileSize?: number;
    /** Element s podpisem */
    signatureElement?: ReactNode;
};

export const FileLink: FunctionComponent<FileLinkProps> = ({
    fileName,
    fileDownloadLink,
    fileSize,
    signatureElement,
    onDownloadRequested,
}) => {
    const clickListenerRef = useStopPropagationEventListenerRef({
        eventType: "click",
        callback: onDownloadRequested,
        preventDefault: true,
    });

    const fileSizeString = useMemo(() => fileSize == null ? null : prettyPrintFileSize(fileSize), [fileSize]);
    const fileNameParts = useMemo(() => splitFileName(fileName), [fileName]);

    return (
        <div className={fileLink} {...createQA(qaMain)}>
            <a
                ref={clickListenerRef}
                className={fileLinkLink}
                {...createQA(qaLink)}
                href={fileDownloadLink}
                title={fileName}
                rel="button">
                <span className={fileLinkName}>{fileNameParts.nameWithoutExt}</span>
                <span className={fileLinkExtension}>{fileNameParts.extensionWithDot}</span>
            </a>
            {
                signatureElement &&
                <Layout qa={qaSignature} spacingSizeLeft={SpacingSize.xxs} flexShrink={FlexShrink.zero} flexGrow={FlexGrow.zero}>
                    {signatureElement}
                </Layout>
            }
            {
                fileSizeString &&
                <Layout qa={qaSize} spacingSizeLeft={SpacingSize.xxs}>
                    <span className={fileLinkSize}>({fileSizeString})</span>
                </Layout>
            }
        </div>
    );
};
